import { useGameStore } from '../store/gameStore';
import { GameMode } from '../types';

export function GameControls() {
  const {
    gameMode,
    setGameMode,
    engineSettings,
    setEngineSettings,
  } = useGameStore();

  const modes: { value: GameMode; label: string; icon: string }[] = [
    { value: 'vs-engine', label: 'vs Engine', icon: '🤖' },
    { value: 'analyze', label: 'Analyze', icon: '🔍' },
  ];

  return (
    <div className="space-y-4">
      {/* Game Mode Selection */}
      <div className="bg-[#312e2b] rounded-xl p-4 shadow-lg">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
          Game Mode
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {modes.map((mode) => (
            <button
              key={mode.value}
              onClick={() => setGameMode(mode.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                gameMode === mode.value
                  ? 'text-white shadow-lg'
                  : 'bg-[#2b2926] text-gray-300 hover:bg-[#3a3633] border border-gray-700'
              }`}
              style={gameMode === mode.value ? { 
                backgroundColor: 'var(--pale-blue)' 
              } : {}}
            >
              <span className="flex items-center justify-center gap-2">
                <span>{mode.icon}</span>
                <span>{mode.label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Engine Selection */}
      <div className="bg-[#312e2b] rounded-xl p-4 shadow-lg">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
          Chess Engine
        </h3>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => setEngineSettings({ engineType: 'stockfish' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                engineSettings.engineType === 'stockfish'
                  ? 'text-white shadow-lg'
                  : 'bg-[#2b2926] text-gray-300 hover:bg-[#3a3633] border border-gray-700'
              }`}
              style={engineSettings.engineType === 'stockfish' ? { 
                backgroundColor: 'var(--pale-blue)' 
              } : {}}
            >
              Stockfish
            </button>
            <button
              onClick={() => setEngineSettings({ engineType: 'maia' })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                engineSettings.engineType === 'maia'
                  ? 'text-white shadow-lg'
                  : 'bg-[#2b2926] text-gray-300 hover:bg-[#3a3633] border border-gray-700'
              }`}
              style={engineSettings.engineType === 'maia' ? { 
                backgroundColor: 'var(--pale-blue)' 
              } : {}}
            >
              Maia
            </button>
          </div>

          {engineSettings.engineType === 'maia' && (
            <div className="space-y-2 pt-2 border-t border-gray-700">
              <label className="text-xs text-gray-400 uppercase tracking-wide">
                Maia Rating Level
              </label>
              <select
                value={engineSettings.maiaLevel || 1500}
                onChange={(e) => setEngineSettings({ maiaLevel: parseInt(e.target.value) as 1100 | 1300 | 1500 | 1700 | 1900 })}
                className="w-full px-3 py-2 bg-[#2b2926] text-white rounded-lg border border-gray-700 text-sm"
              >
                <option value={1100}>1100 - Beginner</option>
                <option value={1300}>1300 - Intermediate</option>
                <option value={1500}>1500 - Advanced</option>
                <option value={1700}>1700 - Expert</option>
                <option value={1900}>1900 - Master</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Maia plays like humans at different skill levels
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Engine Difficulty */}
      {gameMode === 'vs-engine' && engineSettings.engineType === 'stockfish' && (
        <div className="bg-[#312e2b] rounded-xl p-4 shadow-lg">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
            Engine Strength
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-400">Level</span>
              <span className="text-white font-bold">{engineSettings.skill}</span>
            </div>
            <input
              type="range"
              min="0"
              max="20"
              value={engineSettings.skill}
              onChange={(e) => setEngineSettings({ skill: parseInt(e.target.value) })}
              className="w-full h-2 bg-[#2b2926] rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Beginner</span>
              <span>Master</span>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Settings */}
      <div className="bg-[#312e2b] rounded-xl p-4 shadow-lg">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
          Analysis
        </h3>
        <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#3a3633] cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={engineSettings.enabled}
            onChange={(e) => setEngineSettings({ enabled: e.target.checked })}
            className="w-4 h-4 rounded bg-[#2b2926] border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
          />
          <div className="flex-1">
            <div className="text-sm text-gray-200">Live Analysis</div>
            <div className="text-xs text-gray-500">Analyze moves automatically</div>
          </div>
        </label>

        {engineSettings.enabled && (
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-400">Depth</span>
              <span className="text-white font-mono text-xs bg-gray-800 px-2 py-1 rounded">
                {engineSettings.depth}
              </span>
            </div>
            <input
              type="range"
              min="10"
              max="20"
              value={engineSettings.depth}
              onChange={(e) => setEngineSettings({ depth: parseInt(e.target.value) })}
              className="w-full h-2 bg-[#2b2926] rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Faster</span>
              <span>Deeper</span>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
